"use client";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { SearchIcon } from '../icons';
import { useSearchService } from '../../hooks/useSearchService';
import { Card, CardLocale, getCardTitle } from '../../types/card';
import LogoLoader from '../LogoLoader/LogoLoader';
import { getTitleSuffix } from '@/constants';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { CardImage } from '../ui/CardImage/CardImage';
import styles from './HeroSearch.module.css';

export const HeroSearch: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('homepage.hero');
  const locale = useLocale() as CardLocale;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<(Card)[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { performSearch, isLoading } = useSearchService();

  // Handle clicking outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for cards as user types with debounce
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      const results = await performSearch(searchTerm.trim());
      setSearchResults(results || []);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchTerm, performSearch]);

  const { localizeFullPath } = useLocalizedPath();

  const handleSearch = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    router.push(`/giftcards?search=${encodeURIComponent(searchTerm)}`);
    setShowResults(false);
  }, [router, searchTerm]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowResults(value.trim().length >= 2);
  }, []);

  const handleFocus = useCallback(() => {
    if (searchTerm.trim().length >= 2) {
      setShowResults(true);
    }
  }, [searchTerm]);

  return (
    <div className={styles.searchWrapper}>
      <div
        ref={searchRef}
        className={styles.searchRow}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setShowResults(false);
        }}
      >
        <div className={styles.searchFieldWrapper}>
          <form className={styles.searchContainer} onSubmit={handleSearch} role="search">
            <input
              className={styles.searchInput}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleFocus}
              aria-label={t('searchLabel')}
            />
            <button type="submit" className={styles.searchButton}>
              {isLoading ? <LogoLoader size={16} color="var(--color-on-brand)" /> : <SearchIcon style={{ fontSize: 18 }} />}
              {t('searchButton')}
            </button>
          </form>

          <div className={`${styles.searchResults}${showResults && (searchResults?.length > 0 || isLoading) ? ` ${styles.visible}` : ''}`}>
            {isLoading && searchResults.length === 0 ? (
              <div className={styles.noResults}>
                <LogoLoader size={20} color="var(--color-text)" />
                <span style={{ marginLeft: '8px' }}>Searching...</span>
              </div>
            ) : searchResults && searchResults.length === 0 && searchTerm.trim().length >= 2 ? (
              <div className={styles.noResults}>No results found</div>
            ) : (
              searchResults.map(card => (
                <Link
                  key={card._id}
                  href={localizeFullPath(`/giftcards/${card.slug}`)}
                  className={styles.resultItem}
                  onClick={() => setShowResults(false)}
                >
                  <CardImage card={card} locale={locale} size="searchResultSmall" />
                  <div className={styles.resultInfo}>
                    <h4 className={styles.resultTitle}>{getCardTitle(card, locale) + getTitleSuffix()}</h4>
                    <p className={styles.resultBrand}>{card.slug}</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        <button
          className={styles.browseAllButton}
          onClick={() => router.push('/giftcards')}
        >
          {t('browseAllButton')}
        </button>
      </div>
    </div>
  );
};
